import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthSessionDto, AuthSessionOrderPortailDto, CLU_MOBILE_INTERFACE, ROLE_WORKSPACE_USER } from '../app.models';
import { BehaviorSubject, Observable, catchError, lastValueFrom, of, switchMap, throwError } from "rxjs";
import { RSAHelper } from "../utils/rsa-helper";
import { ApiService } from "../services/api.service";
import { AppService } from "../services/app.service";
import { GetNetworkErrorMessage, HandleError } from "../utils/network-error-handler";
import { CashDeskCoreRxDbService } from "../@clu-fe/apps/m-cashdesk/@services/m-cashdesk-core-rxdb.service";
import { v4 as uuidv4} from 'uuid';
import { SignalRService } from "../services/signalr.service";

const SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY = "SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    isAuthenticated = false;
    onUserAuthenticated$ = new BehaviorSubject<boolean>(false);
    authenticatedSession$ = new BehaviorSubject<AuthSessionDto | null>(null);
    isSessionLocked$ = new BehaviorSubject<boolean>(false);

    constructor(
        private _httpClient: HttpClient,
        private _appService: AppService,
        private _apiService: ApiService,
        private _rsaHelper: RSAHelper,
        private _cashDeskCoreRxDbService: CashDeskCoreRxDbService,
        private _signalrService: SignalRService
    ) {
        this.checkIfUserIsAuthenticated();
    }

    async checkIfUserIsAuthenticated() {
        const authSession = this.AuthSession;
        this.isAuthenticated = !!authSession;
        this.onUserAuthenticated$.next(this.isAuthenticated);
        this.authenticatedSession$.next(authSession);
        this.isSessionLocked$.next(authSession?.IS_LOCKED == true);
    }

    /*set AuthSession(_session: AuthSessionDto | null) {
        localStorage.setItem(SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY, _session !== null ? JSON.stringify(_session) : 'null');
        this.persistSessionInLocalDb(_session);
    }*/

    set AuthSession(_session: AuthSessionDto | null) {
        this.setAuthSession(_session).catch(error => { console.error('Failed to persist session:', error); });
    }

    private async setAuthSession(_session: AuthSessionDto | null, disablePersistence?: boolean): Promise<void> {
        localStorage.setItem(SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY, _session !== null ? JSON.stringify(_session) : 'null');
        if (disablePersistence == true) { return; }

        await this.persistSessionInLocalDb(_session);
    }

    get AuthSession(): AuthSessionDto | null {
        const lsAuthSessionStr = localStorage.getItem(SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY);
        if (lsAuthSessionStr === 'undefined' || lsAuthSessionStr === 'null') { return null; }

        let lsAuthSession: AuthSessionDto | null = null;
        try { lsAuthSession = JSON.parse(lsAuthSessionStr!); } catch (e) { }
        return lsAuthSession;
    }

    set AccessToken(token: string) {
        const authSession = this.authenticatedSession$.value
        if (!!authSession) {
            authSession.AccessToken = token;
            this.AuthSession = authSession;
        }
    }

    async getAuthPermissions(): Promise<any[]> {
        if (!this._cashDeskCoreRxDbService.db || !this._cashDeskCoreRxDbService.db.cluauthsession) { return []; }

        const authSession = this.AuthSession;
        if (!authSession) { return []; }

        const inDbSession = await this._cashDeskCoreRxDbService.db.cluauthsession.findOne({ selector: { "SESSION_ID" : { "$eq": !!authSession ? authSession.SESSION_ID : null } }, sort: [] }).exec();
        if (!inDbSession || !inDbSession.USER_PERMS) { return []; }

        let userPermissions = [];
        try { userPermissions = JSON.parse(inDbSession.USER_PERMS); } catch { }
        return userPermissions;
    }

    async persistSessionInLocalDb(_session: AuthSessionDto | null) {
        if (!this._cashDeskCoreRxDbService.db || !this._cashDeskCoreRxDbService.db.cluauthsession) { return; }
        if (!!_session) {
            await this._cashDeskCoreRxDbService.db.cluauthsession
                    .findOne({ selector: { "SESSION_ID" : { "$eq": !!_session ? _session.SESSION_ID : null } }, sort: [] })
                    .exec().then(async(oldSession) => {
                        if (!!oldSession) {
                            await oldSession.patch({
                                USER_ID: _session.ID,
                                USER_PERMS: JSON.stringify(_session.USER_PERMS),
                                CODE_PIN: _session.CODE_PIN,
                                IS_LOCKED: _session.IS_LOCKED ? 1 : 0,
                            });
                        } else {
                            await this._cashDeskCoreRxDbService.db.cluauthsession.find().remove();
                            await this._cashDeskCoreRxDbService.db.cluauthsession.insert({
                                SESSION_ID: uuidv4(),
                                USER_PERMS: JSON.stringify(_session.USER_PERMS),
                                CODE_PIN: _session.CODE_PIN,
                                IS_LOCKED: _session.IS_LOCKED == true ? 1 : 0
                            });
                        }
                    });
        } else {
            await this._cashDeskCoreRxDbService.db.cluauthsession.find().remove();
        }
    }

    doLockSession() {
        const authSession = this.authenticatedSession$.value
        if (!!authSession) {
            authSession.IS_LOCKED = true;
            this.AuthSession = authSession;
            this.isSessionLocked$.next(true);
        }
    }

    doUnlockSession() {
        const authSession = this.authenticatedSession$.value
        if (!!authSession) {
            authSession.IS_LOCKED = false;
            this.AuthSession = authSession;
            this.isSessionLocked$.next(false);
        }
    }

    saveAuthenticationSession(authSession: AuthSessionDto, disablePersistence?: boolean) {
        this.setAuthSession(authSession, disablePersistence);
        this.isAuthenticated = true;
        this.onUserAuthenticated$.next(true);
        this.authenticatedSession$.next(authSession);
    }

    doAuthenticateAdminAction(_username: string, _password: string) {
        return lastValueFrom(this._httpClient.post<string>(this._apiService.endPointsBO.doLoginAction_BOAdmAuth, { UserName: this._rsaHelper.encryptWithPublicKey(_username), Password: this._rsaHelper.encryptWithPublicKey(_password) }, { responseType: 'text' as any })
            .pipe(catchError(error => {
                if (!!error.error) {
                    try { error.error = JSON.parse(error.error); } catch {}
                }
                return lastValueFrom(HandleError(error));
            })));
    }

    doSignoutAdminAction() {
        return lastValueFrom(this._httpClient.post<any>(this._apiService.endPointsBO.doSignoutAction_BAdmAuth, {}));
    }

    doGetAuthAdminSession(accessToken: string) {
        return lastValueFrom(this._httpClient.get<AuthSessionDto>(this._apiService.endPointsBO.doGetAuthSession_BOAdmAuth, { headers: { Authorization: `Bearer ${accessToken}` } })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }

    doAuthenticateAction(_codeSite: string, _username: string, _password: string) {
        return lastValueFrom(this._httpClient.post<string>(this._apiService.endPointsFO.doLoginAction_FOAuth, { CodeSite: _codeSite, UserName: this._rsaHelper.encryptWithPublicKey(_username), Password: this._rsaHelper.encryptWithPublicKey(_password) }, { responseType: 'text' as any })
            .pipe(catchError(error => {
                if (!!error.error) {
                    try { error.error = JSON.parse(error.error); } catch {}
                }
                return lastValueFrom(HandleError(error));
            })));
    }

    doSignoutFOAction() {
        return lastValueFrom(this._httpClient.post<any>(this._apiService.endPointsFO.doSignoutAction_FOAuth, {}));
    }

    doGetAuthSession(accessToken: string) {
        return lastValueFrom(this._httpClient.get<AuthSessionDto>(this._apiService.endPointsFO.doGetAuthSession_FOAuth, { headers: { Authorization: `Bearer ${accessToken}` } })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }

    doAuthenticateMobileAction(_domaine: string, _codeSite: string | null, _username: string, _password: string, _interfaceId: CLU_MOBILE_INTERFACE) {
        return lastValueFrom(this._httpClient.post<string>(this._apiService.endPointsMobile.doLoginAction_MobileAuth, {
            Domaine: _domaine,
            CodeSite: _codeSite,
            UserName: this._rsaHelper.encryptWithPublicKey(_username),
            Password: this._rsaHelper.encryptWithPublicKey(_password),
            InterfaceId: _interfaceId,
        }, { responseType: 'text' as any })
            .pipe(catchError(error => {
                if (!!error.error) {
                    try { error.error = JSON.parse(error.error); } catch {}
                }
                return lastValueFrom(HandleError(error));
            })));
    }

    doGetAuthMobileSession(accessToken: string, cluInterface: CLU_MOBILE_INTERFACE) {
        let httpParams = new HttpParams().set("CluInterface", cluInterface.toString());
        return lastValueFrom(this._httpClient.get<AuthSessionDto>(this._apiService.endPointsMobile.doGetAuthSession_MobileAuth, { params: httpParams, headers: { Authorization: `Bearer ${accessToken}`, 'X-CLU-INTERFACE-ID': 'X-CLU-INTERFACE-ID ' + cluInterface.toString() } })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }

    doAuthenticateOrderPortailAction(_codeSite: string, _username: string, _password: string) {
        return lastValueFrom(this._httpClient.post<string>(this._apiService.endPointsFO.doLoginAction_FOPOrderPortailAuth, { CodeSite: _codeSite, UserName: this._rsaHelper.encryptWithPublicKey(_username), Password: this._rsaHelper.encryptWithPublicKey(_password) }, { responseType: 'text' as any })
            .pipe(catchError(error => {
                if (!!error.error) {
                    try { error.error = JSON.parse(error.error); } catch {}
                }
                return lastValueFrom(HandleError(error));
            })));
    }

    doGetAuthOrderPortailSession(accessToken: string) {
        return lastValueFrom(this._httpClient.get<AuthSessionOrderPortailDto>(this._apiService.endPointsFO.doGetAuthSession_FOPOrderPortailAuth, { headers: { Authorization: `Bearer ${accessToken}` } })
        .pipe(catchError(error => lastValueFrom(HandleError(error)))));
    }

    doRefreshToken(): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            const authSession = this.AuthSession;
            let url = authSession!.ROLE_ID == ROLE_WORKSPACE_USER.SUPER_ADMIN_WORKSPACE || authSession!.ROLE_ID == ROLE_WORKSPACE_USER.ADMIN_WORKSPACE ?
                    this._apiService.endPointsBO.doRefreshTokenAction_BOAdmAuth :
                    this._apiService.endPointsFO.doRefreshTokenAction_FOAuth;

            if (this._appService.isMobileApp) {
                url = this._apiService.endPointsMobile.doRefreshTokenAction_MobileAuth;
            }

            this._httpClient.post<AuthSessionDto>(url, {
                AccessToken: authSession!.AccessToken,
                RefreshToken: authSession!.RefreshToken
            },
            { headers: { Authorization: 'Bearer ' + authSession!.AccessToken }})
            .subscribe(_response => {
                this.saveAuthenticationSession(_response);
                resolve(true);
            }, (error) => {
                reject(GetNetworkErrorMessage(error));
            });
        })
    }

    doRemoveAuthSession() {
        this.AuthSession = null;
        this.isAuthenticated = false;
        this.onUserAuthenticated$.next(false);
        this.authenticatedSession$.next(null);
        this.isSessionLocked$.next(false);
        this._signalrService.unauthenticate();

        return of(true);
    }
}
